// Local-only editor for solo browser remix: same propose() interface as
// useJamEditor, but applies intents in-memory via the merge engine — no
// WebSocket, no server, no auth.

import { useCallback, useMemo, useRef, useState } from "react";
import type { Intent } from "@shared/sync/intents.js";
import { cloneRoom, type CanonicalRoom } from "@shared/sync/state.js";
import { mergeIntents } from "@shared/sync/merge-engine.js";
import { coalesceIntents } from "@web-shared/lib/edits";
import { describeIntents, rebornIds, recordUndo, remapEntry, undoPlan, type UndoEntry } from "@web-shared/lib/undo";

const MAX_UNDO = 100;

export interface LocalEditor {
  room: CanonicalRoom | null;
  /** How many edits deep since the seed was opened (0 = untouched). */
  editDepth: number;
  dirty: boolean;
  /** Apply intents locally; returns true when something was accepted. */
  propose: (intents: Intent[]) => boolean;
  /** The same, for what the page does by itself (a render): nothing to undo. */
  proposeUnrecorded: (intents: Intent[]) => boolean;
  /** A multi-step edit is still in flight and the room has not settled. Local
   *  mode is synchronous, so it is never busy; the connected editor is, and a
   *  held arrow key must not outrun it. */
  busy?: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  undo: () => void;
  redo: () => void;
  /** Reset dirty tracking (e.g. after a successful publish). */
  markClean: () => void;
}


export function useLocalEditor(initial: CanonicalRoom | null): LocalEditor {
  const [room, setRoom] = useState<CanonicalRoom | null>(initial);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<UndoEntry[]>([]);
  const [editDepth, setEditDepth] = useState(0);
  const [cleanDepth, setCleanDepth] = useState(0);
  const roomRef = useRef(room);
  roomRef.current = room;
  const undoRef = useRef(undoStack);
  undoRef.current = undoStack;
  const redoRef = useRef(redoStack);
  redoRef.current = redoStack;

  // Adopt new initial room when it first loads (null → room).
  useMemo(() => {
    if (initial && !roomRef.current) {
      setRoom(initial);
      roomRef.current = initial;
    }
  }, [initial]);

  // Random, not a counter: a room restored from a draft already holds ids
  // minted before the reload, and a counter starting again at 1 would mint
  // them a second time.
  const mintId = useCallback((kind: "track" | "clip") => {
    return `local_${kind[0]}_${crypto.randomUUID().slice(0, 12)}`;
  }, []);

  const applyMerge = useCallback(
    (intents: Intent[], history: "push" | "undo" | "redo" | "none", reborn?: Map<string, string>): boolean => {
      const current = roomRef.current;
      if (!current) return false;
      const coalesced = coalesceIntents(intents);
      if (coalesced.length === 0) return false;

      const snapshot = cloneRoom(current);
      const result = mergeIntents(snapshot, coalesced, {
        newId: mintId,
        audioExists: () => true, // local mode: trust all keys
      });
      if (result.accepted.length === 0) return false;

      setRoom(snapshot);
      roomRef.current = snapshot;

      // An undone delete comes back under a new id: re-point the history at
      // it, or the entries that name the old one silently do nothing.
      const ids = reborn ? rebornIds(reborn, result.accepted) : null;
      if (ids && ids.size > 0) {
        setUndoStack((prev) => prev.map((e) => remapEntry(e, ids)));
        setRedoStack((prev) => prev.map((e) => remapEntry(e, ids)));
      }

      const label = describeIntents(coalesced);
      const entry = recordUndo(current, result.accepted, label);

      if (history === "push") {
        if (entry) {
          setUndoStack((prev) => [...prev.slice(-(MAX_UNDO - 1)), entry]);
          setRedoStack([]);
        }
        setEditDepth((d) => d + 1);
      } else if (history === "undo") {
        if (entry) setRedoStack((prev) => [...prev, entry]);
      } else if (history === "redo") {
        if (entry) setUndoStack((prev) => [...prev, entry]);
      } else {
        setEditDepth((d) => d + 1); // nothing to undo, but still unsaved
      }

      return true;
    },
    [mintId],
  );

  const propose = useCallback(
    (intents: Intent[]): boolean => applyMerge(intents, "push"),
    [applyMerge],
  );
  const proposeUnrecorded = useCallback(
    (intents: Intent[]): boolean => applyMerge(intents, "none"),
    [applyMerge],
  );

  const undo = useCallback(() => {
    const stack = undoRef.current;
    if (stack.length === 0) return;
    const entry = stack[stack.length - 1]!;
    setUndoStack((prev) => prev.slice(0, -1));
    const current = roomRef.current;
    if (!current) return;
    const plan = undoPlan(current, entry);
    if (plan.intents.length > 0) applyMerge(plan.intents, "undo", plan.reborn);
  }, [applyMerge]);

  const redo = useCallback(() => {
    const stack = redoRef.current;
    if (stack.length === 0) return;
    const entry = stack[stack.length - 1]!;
    setRedoStack((prev) => prev.slice(0, -1));
    const current = roomRef.current;
    if (!current) return;
    const plan = undoPlan(current, entry);
    if (plan.intents.length > 0) applyMerge(plan.intents, "redo", plan.reborn);
  }, [applyMerge]);

  const markClean = useCallback(() => {
    setCleanDepth(editDepth);
  }, [editDepth]);

  return {
    room,
    editDepth,
    dirty: editDepth !== cleanDepth,
    propose,
    proposeUnrecorded,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoLabel: undoStack.at(-1)?.label ?? null,
    redoLabel: redoStack.at(-1)?.label ?? null,
    undo,
    redo,
    markClean,
  };
}
